import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeolocation } from '../hooks/useGeolocation';
import { useDeviceOrientation } from '../hooks/useDeviceOrientation';
import { useSignalR, HuntCompletedMessage, PinDiscoveredMessage, ChallengeEncounteredMessage, ChallengeSolvedMessage, ItemFoundMessage, ValidationRequestedMessage, ValidationApprovedMessage, ValidationApprovedWithChallengeMessage, ValidationRejectedMessage } from '../hooks/useSignalR';
import { hunterService, HunterPinInfo } from '../services/api/hunterService';
import { huntService } from '../services/api/huntService';
import { pinService, ChallengeState } from '../services/api/pinService';
import { validationService } from '../services/api/validationService';
import { useLocationStore, useHunterLocations } from '../stores/locationStore';
import { useAuthStore } from '../stores/authStore';
import { useHuntStore } from '../stores/huntStore';
import { calculateDistance, calculateBearing, isWithinProximity } from '../utils/gps';
import { CompassDisplay, OtherHunter } from '../components/compass/CompassDisplay';
import { ConnectionStatusBanner } from '../components/common/ConnectionStatus';
import { HuntDeletedModal, VictoryCelebration, VictoryHeader } from '../components/common';
import { HunterChallengeModal } from '../components/hunter/HunterChallengeModal';
import { ChallengeSurpriseReveal } from '../components/hunter/ChallengeSurpriseReveal';

// Local type definition (mirrors @gps-hunt/shared)
type ChallengeType = 'riddle' | 'hex_code' | 'numerical';

const PROXIMITY_THRESHOLD = 25; // meters
const PROXIMITY_HYSTERESIS = 5; // meters - prevents flickering at boundary
const LOCATION_UPDATE_INTERVAL = 3000; // 3 seconds for SignalR broadcasts

/**
 * Hunter page with compass navigation to pins
 */
export function HunterPage() {
  const { huntId } = useParams<{ huntId: string }>();
  const navigate = useNavigate();

  // Location and orientation hooks
  const {
    position,
    error: geoError,
    isLoading: geoLoading,
    locationDenied,
    startWatching,
    stopWatching,
    isWatching,
  } = useGeolocation({ enableHighAccuracy: true });

  const {
    heading,
    error: compassError,
    isActive: compassActive,
    startListening,
    stopListening,
  } = useDeviceOrientation();

  // State
  const [pinInfo, setPinInfo] = useState<HunterPinInfo | null>(null);
  const [discoveredPin, setDiscoveredPin] = useState<{
    pinId: string;
    hintPhotoUrl: string | null;
    itemCount: number;
  } | null>(null);
  const [foundItemCount, setFoundItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [huntComplete, setHuntComplete] = useState(false);
  const [completionTime, setCompletionTime] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [huntDeletedInfo, setHuntDeletedInfo] = useState<{ huntName: string; message: string } | null>(null);
  const [awaitingValidation, setAwaitingValidation] = useState(false);
  
  // Challenge state
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showSurpriseReveal, setShowSurpriseReveal] = useState(false);
  const [isCaptain, setIsCaptain] = useState(false);
  const [captainInfo, setCaptainInfo] = useState<{ userId: string; displayName: string } | null>(null);
  const [challengeInfo, setChallengeInfo] = useState<{
    pinId: string;
    type: ChallengeType;
    question: string;
    state: ChallengeState | null;
  } | null>(null);

  // iOS compass permission state - no longer needed since we use GPS-only navigation
  // Keeping the state for backwards compatibility but always granting
  const [compassPermissionGranted] = useState(true);

  // Buddy mode state (for GPS-denied hunters)
  const [buddyMode, setBuddyMode] = useState(false);
  const [buddyName, setBuddyName] = useState<string | null>(null);
  const [showBuddyPicker, setShowBuddyPicker] = useState(false);
  const [buddyPickerParticipants, setBuddyPickerParticipants] = useState<{ userId: string; displayName: string }[]>([]);
  const [buddyPickerLoading, setBuddyPickerLoading] = useState(false);
  const [buddyPickerError, setBuddyPickerError] = useState<string | null>(null);

  // Location store
  const { setMyLocation, clearLocations } = useLocationStore();
  const hunterLocations = useHunterLocations();
  const currentUserId = useAuthStore((state) => state.user?.uid);
  const resetHuntStore = useHuntStore((state) => state.reset);

  // Refs to hold values for SignalR handlers (avoid stale closures)
  const loadPinInfoRef = useRef<() => Promise<void>>();
  const discoveredPinRef = useRef<typeof discoveredPin>(null);
  const currentUserIdRef = useRef(currentUserId);
  const huntIdRef = useRef(huntId);

  // Keep refs in sync with state (for SignalR handlers)
  useEffect(() => {
    discoveredPinRef.current = discoveredPin;
  }, [discoveredPin]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);
  useEffect(() => {
    huntIdRef.current = huntId;
  }, [huntId]);

  // SignalR handler callbacks - using refs to avoid stale closures
  const handleHuntDeleted = useCallback((msg: { huntName: string; message: string }) => {
    resetHuntStore();
    setHuntDeletedInfo({ huntName: msg.huntName, message: msg.message });
  }, [resetHuntStore]);

  const handleHuntCompleted = useCallback((_msg: HuntCompletedMessage) => {
    const hId = huntIdRef.current;
    if (hId) {
      navigate(`/stats/${hId}`, { replace: true });
    }
  }, [navigate]);

  const handlePinDiscovered = useCallback((_msg: PinDiscoveredMessage) => {
    loadPinInfoRef.current?.();
  }, []);

  const onItemFoundSignalR = useCallback((msg: ItemFoundMessage) => {
    const currentDiscoveredPin = discoveredPinRef.current;
    if (currentDiscoveredPin?.pinId === msg.pinId) {
      setFoundItemCount(msg.totalFound);
    }
  }, []);

  const handleChallengeEncountered = useCallback((msg: ChallengeEncounteredMessage) => {
    const currentDiscoveredPin = discoveredPinRef.current;
    const userId = currentUserIdRef.current;
    if (msg.captainUserId !== userId && currentDiscoveredPin?.pinId === msg.pinId) {
      setChallengeInfo({
        pinId: msg.pinId,
        type: msg.challengeType as ChallengeType,
        question: msg.challengeQuestion,
        state: null,
      });
      setCaptainInfo({ userId: msg.captainUserId, displayName: msg.captainDisplayName });
      setIsCaptain(false);
      setShowSurpriseReveal(true);
    }
  }, []);

  const handleChallengeSolved = useCallback(async (msg: ChallengeSolvedMessage) => {
    console.log('[HunterPage] Challenge solved:', msg);
    
    // Delay closing the modal to let the celebration animation complete (3 seconds)
    // The ChallengeSurpriseReveal component shows a 3-second unlock celebration
    setTimeout(() => {
      setShowSurpriseReveal(false);
      setShowChallengeModal(false);
      setChallengeInfo(null);
      setCaptainInfo(null);
      setIsCaptain(false);
    }, 3500);
    
    const hId = huntIdRef.current;
    if (hId && msg.pinId) {
      // Delay pin completion to happen after celebration
      setTimeout(async () => {
        try {
          const result = await hunterService.completePin(hId, msg.pinId);
          if (result.huntComplete) {
            setHuntComplete(true);
            setCompletionTime(result.completionTime || new Date().toISOString());
          } else {
            loadPinInfoRef.current?.();
          }
        } catch (err) {
          console.error('Failed to auto-complete pin after challenge:', err);
          loadPinInfoRef.current?.();
        }
      }, 3500);
    }
  }, []);

  // Validation handlers - for hider referee feature
  const handleValidationRequested = useCallback((_msg: ValidationRequestedMessage) => {
    // Another hunter requested validation - we should also show waiting state
    const currentDiscoveredPin = discoveredPinRef.current;
    if (currentDiscoveredPin?.pinId === _msg.pinId) {
      setAwaitingValidation(true);
    }
  }, []);

  const handleValidationApproved = useCallback((_msg: ValidationApprovedMessage) => {
    setAwaitingValidation(false);
    // Pin completed - load next pin
    loadPinInfoRef.current?.();
  }, []);

  const handleValidationApprovedWithChallenge = useCallback((msg: ValidationApprovedWithChallengeMessage) => {
    setAwaitingValidation(false);
    // Hider approved but there's a challenge - trigger challenge encounter
    const currentDiscoveredPin = discoveredPinRef.current;
    const user = useAuthStore.getState().user;
    if (currentDiscoveredPin?.pinId === msg.pinId && user) {
      // Set up challenge info and show surprise reveal
      setChallengeInfo({
        pinId: msg.pinId,
        type: msg.challengeType as ChallengeType,
        question: msg.challengeQuestion,
        state: null,
      });
      setShowSurpriseReveal(true);
      // Call encounter to determine captain
      pinService.encounterChallenge(
        huntIdRef.current!,
        msg.pinId,
        user.uid,
        user.displayName || 'Hunter'
      ).then(result => {
        setIsCaptain(result.isCaptain || false);
        if (!result.isCaptain && result.state?.lockedByUserId) {
          setCaptainInfo({
            userId: result.state.lockedByUserId || 'unknown',
            displayName: result.state.lockedByDisplayName || 'Another Hunter',
          });
        } else {
          setCaptainInfo(null);
        }
        setChallengeInfo(prev => prev ? { ...prev, state: result.state || null } : null);
      }).catch(console.error);
    }
  }, []);

  const handleValidationRejected = useCallback((msg: ValidationRejectedMessage) => {
    setAwaitingValidation(false);
    // Item rejected - update count
    const currentDiscoveredPin = discoveredPinRef.current;
    if (currentDiscoveredPin?.pinId === msg.pinId) {
      setFoundItemCount(msg.newFoundCount);
    }
  }, []);

  // SignalR real-time connection with handlers
  const {
    connectionStatus,
    isConnected,
    connect: connectSignalR,
    disconnect: disconnectSignalR,
    forceReconnect,
    sendLocation,
  } = useSignalR({
    onHuntDeleted: handleHuntDeleted,
    onHuntCompleted: handleHuntCompleted,
    onPinDiscovered: handlePinDiscovered,
    onItemFound: onItemFoundSignalR,
    onChallengeEncountered: handleChallengeEncountered,
    onChallengeSolved: handleChallengeSolved,
    onValidationRequested: handleValidationRequested,
    onValidationApproved: handleValidationApproved,
    onValidationApprovedWithChallenge: handleValidationApprovedWithChallenge,
    onValidationRejected: handleValidationRejected,
    // Re-fetch state after reconnection to restore found items, discovery state, etc.
    onReconnected: () => {
      console.log('[HunterPage] Reconnected — re-fetching pin state');
      loadPinInfoRef.current?.();
    },
  });

  // Debounced proximity state to prevent GPS drift flickering
  const [debouncedInProximity, setDebouncedInProximity] = useState(false);
  const lastProximityState = useRef(false);

  // Calculate distance and bearing to current pin
  const { distance, bearing, isInProximity } = useMemo(() => {
    if (!position || !pinInfo?.currentPin) {
      return { distance: null, bearing: 0, isInProximity: false };
    }

    const dist = calculateDistance(
      { latitude: position.latitude, longitude: position.longitude },
      { latitude: pinInfo.currentPin.latitude, longitude: pinInfo.currentPin.longitude }
    );

    const bear = calculateBearing(
      { latitude: position.latitude, longitude: position.longitude },
      { latitude: pinInfo.currentPin.latitude, longitude: pinInfo.currentPin.longitude }
    );

    return {
      distance: dist,
      bearing: bear,
      isInProximity: isWithinProximity(dist, PROXIMITY_THRESHOLD),
    };
  }, [position, pinInfo?.currentPin]);

  // Calculate other hunters for compass display
  const otherHunters = useMemo<OtherHunter[]>(() => {
    if (!position) return [];

    return hunterLocations
      .filter((hunter) => {
        // Exclude self
        if (hunter.userId === currentUserId) return false;
        // Only show hunters (not hiders)
        if (hunter.role !== 'hunter') return false;
        // Only show connected hunters
        if (hunter.connectionStatus === 'disconnected') return false;
        return true;
      })
      .map((hunter) => ({
        userId: hunter.userId,
        displayName: hunter.displayName,
        bearing: calculateBearing(
          { latitude: position.latitude, longitude: position.longitude },
          { latitude: hunter.latitude, longitude: hunter.longitude }
        ),
        distance: calculateDistance(
          { latitude: position.latitude, longitude: position.longitude },
          { latitude: hunter.latitude, longitude: hunter.longitude }
        ),
        connectionStatus: hunter.connectionStatus,
      }))
      .slice(0, 6); // Max 6 hunters on compass
  }, [position, hunterLocations, currentUserId]);

  // Debounce proximity state with hysteresis to prevent GPS drift flickering
  useEffect(() => {
    if (distance === null) return;

    // Hysteresis: once in proximity, stay in proximity until we're beyond threshold + hysteresis
    // This prevents flickering when GPS oscillates around the 25m boundary
    if (isInProximity && !lastProximityState.current) {
      // Entering proximity zone
      lastProximityState.current = true;
      setDebouncedInProximity(true);
    } else if (!isInProximity && lastProximityState.current) {
      // Only exit proximity if we're beyond threshold + hysteresis
      if (distance > PROXIMITY_THRESHOLD + PROXIMITY_HYSTERESIS) {
        lastProximityState.current = false;
        setDebouncedInProximity(false);
      }
    } else if (isInProximity) {
      // Ensure we're in proximity state
      setDebouncedInProximity(true);
    }
  }, [distance, isInProximity]);

  // Load pin info
  const loadPinInfo = useCallback(async () => {
    if (!huntId) return;

    try {
      setIsLoading(true);
      const info = await hunterService.getCurrentPin(huntId);
      setPinInfo(info);
      setError(null);

      // Detect existing buddy pairing from server (persisted across reconnects)
      if (info.buddyUserId && info.buddyDisplayName) {
        setBuddyMode(true);
        setBuddyName(info.buddyDisplayName);
      }

      // Restore discovered state from server (for reconnect/refresh)
      if (info.isDiscovered && info.currentPin) {
        setDiscoveredPin({
          pinId: info.currentPin.id,
          hintPhotoUrl: info.hintPhotoUrl ?? info.currentPin.hintPhotoUrl,
          itemCount: info.currentPin.itemCount,
        });
        setFoundItemCount(info.foundItemsAtCurrentPin ?? 0);

        // Restore challenge state if there's an active unsolved challenge
        if (info.challengeState && info.challengeState.isActive && !info.challengeState.isSolved && info.challengeState.challenge) {
          const userId = currentUserIdRef.current;
          const amCaptain = info.challengeState.lockedByUserId === userId;
          setChallengeInfo({
            pinId: info.currentPin.id,
            type: info.challengeState.challenge.type as ChallengeType,
            question: info.challengeState.challenge.question,
            state: {
              pinId: info.currentPin.id,
              isActive: info.challengeState.isActive,
              isSolved: info.challengeState.isSolved,
              lockedByUserId: info.challengeState.lockedByUserId,
              lockedByDisplayName: info.challengeState.lockedByDisplayName,
            },
          });
          setIsCaptain(amCaptain);
          if (!amCaptain && info.challengeState.lockedByUserId) {
            setCaptainInfo({
              userId: info.challengeState.lockedByUserId,
              displayName: info.challengeState.lockedByDisplayName || 'Another Hunter',
            });
          } else {
            setCaptainInfo(null);
          }
          // Show the challenge modal (skip surprise reveal on reconnect)
          setShowChallengeModal(true);
          setShowSurpriseReveal(false);
        }
      } else {
        // Reset discovered state when moving to new (undiscovered) pin
        setDiscoveredPin(null);
        // Initialize found item count based on server state
        setFoundItemCount(info.foundItemsAtCurrentPin ?? 0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pin info');
    } finally {
      setIsLoading(false);
    }
  }, [huntId]);

  // Keep ref updated for SignalR handler
  loadPinInfoRef.current = loadPinInfo;

  // Initialize
  useEffect(() => {
    loadPinInfo();
    startWatching();
    
    // Only auto-start compass on non-iOS devices
    // On iOS, we need user gesture first (handled by CompassPermissionGate)
    if (compassPermissionGranted) {
      startListening();
    }

    // Connect to SignalR
    if (huntId) {
      connectSignalR(huntId);
    }

    return () => {
      stopWatching();
      stopListening();
      disconnectSignalR();
      clearLocations();
    };
  }, [loadPinInfo, startWatching, stopWatching, startListening, stopListening, huntId, connectSignalR, disconnectSignalR, clearLocations, compassPermissionGranted]);

  // Broadcast location via SignalR
  useEffect(() => {
    if (!huntId || !position || !isWatching || !isConnected) return;

    // Update local store
    setMyLocation({
      latitude: position.latitude,
      longitude: position.longitude,
      accuracy: position.accuracy,
      heading: position.heading ?? undefined,
      speed: position.speed ?? undefined,
    });

    // Broadcast immediately on position change, throttled
    const interval = setInterval(() => {
      sendLocation(huntId, {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        heading: position.heading,
        speed: position.speed,
      });
    }, LOCATION_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [huntId, position, isWatching, isConnected, sendLocation, setMyLocation]);

  // Auto-discover pin when in proximity (using debounced state)
  useEffect(() => {
    if (!huntId || !pinInfo?.currentPin || !position || !debouncedInProximity || discoveredPin) {
      return;
    }

    // Discover the pin
    hunterService
      .discoverPin(huntId, pinInfo.currentPin.id, {
        latitude: position.latitude,
        longitude: position.longitude,
      })
      .then((result) => {
        setDiscoveredPin({
          pinId: result.pinId,
          hintPhotoUrl: result.hintPhotoUrl,
          itemCount: result.itemCount,
        });
        // Initialize found item count from server
        setFoundItemCount(result.foundItemsAtCurrentPin ?? 0);
      })
      .catch(console.error);
  }, [huntId, pinInfo?.currentPin, position, debouncedInProximity, discoveredPin]);

  // Handle item found - uses current count as the item index
  const handleItemFound = useCallback(
    async () => {
      if (!huntId || !discoveredPin) return;
      if (foundItemCount >= discoveredPin.itemCount) return; // Already found all items

      // Clear any previous error
      setItemError(null);

      try {
        // Use current count as the item index (server just increments count)
        const result = await hunterService.markItemFound(huntId, discoveredPin.pinId, foundItemCount);
        setFoundItemCount(result.totalFoundAtPin);
        
        // Challenge check is now done via encounterChallenge when clicking "Complete Pin"
        // This keeps the challenge as a surprise until the hunter thinks they're done!
      } catch (err) {
        console.error('Failed to mark item found:', err);
        // Show user-facing error
        setItemError('Failed to record item. Tap again to retry.');
        // Auto-clear error after 3 seconds
        setTimeout(() => setItemError(null), 3000);
      }
    },
    [huntId, discoveredPin, foundItemCount]
  );

  // Challenge handlers
  const handleChallengeLock = useCallback(async () => {
    if (!huntId || !challengeInfo) return;
    
    const user = useAuthStore.getState().user;
    if (!user) return;
    
    const result = await pinService.lockChallenge(
      huntId, 
      challengeInfo.pinId, 
      user.uid, 
      user.displayName || 'Unknown'
    );
    
    if (result.success && result.state) {
      setChallengeInfo(prev => prev ? { ...prev, state: result.state! } : null);
    }
  }, [huntId, challengeInfo]);

  const handleChallengeUnlock = useCallback(async () => {
    if (!huntId || !challengeInfo) return;
    
    const user = useAuthStore.getState().user;
    if (!user) return;
    
    await pinService.unlockChallenge(huntId, challengeInfo.pinId, user.uid);
    setChallengeInfo(prev => prev ? { ...prev, state: null } : null);
  }, [huntId, challengeInfo]);

  const handleChallengeSubmit = useCallback(async (answer: string): Promise<{ correct: boolean; message: string }> => {
    if (!huntId || !challengeInfo) {
      return { correct: false, message: 'Challenge not found' };
    }
    
    const user = useAuthStore.getState().user;
    if (!user) {
      return { correct: false, message: 'Not authenticated' };
    }
    
    const result = await pinService.submitChallengeAnswer(
      huntId,
      challengeInfo.pinId,
      user.uid,
      user.displayName || 'Unknown',
      answer
    );
    
    if (result.correct && result.state) {
      setChallengeInfo(prev => prev ? { ...prev, state: result.state! } : null);
    }
    
    return { correct: result.correct, message: result.message };
  }, [huntId, challengeInfo]);

  // Handle pin complete - now requests validation from hiders first
  const handlePinComplete = useCallback(async () => {
    if (!huntId || !discoveredPin) return;

    const user = useAuthStore.getState().user;
    if (!user) return;

    try {
      setIsLoading(true);
      
      // Request validation from hiders
      await validationService.requestValidation(huntId, discoveredPin.pinId);
      
      // Wait for hider response via SignalR
      setAwaitingValidation(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request validation');
    } finally {
      setIsLoading(false);
    }
  }, [huntId, discoveredPin]);

  const handleChallengeClose = useCallback(() => {
    setShowChallengeModal(false);
    setShowSurpriseReveal(false);
    // If challenge was solved, complete the pin
    if (challengeInfo?.state?.isSolved) {
      // Reset challenge state and reload pin
      setChallengeInfo(null);
      setCaptainInfo(null);
      setIsCaptain(false);
      loadPinInfo();
    } else {
      setChallengeInfo(null);
      setCaptainInfo(null);
      setIsCaptain(false);
    }
  }, [challengeInfo, loadPinInfo]);

  // Handle surprise reveal submission
  const handleSurpriseSubmit = useCallback(async (answer: string): Promise<{ correct: boolean; message: string }> => {
    if (!huntId || !challengeInfo) {
      return { correct: false, message: 'No active challenge' };
    }

    const user = useAuthStore.getState().user;
    if (!user) {
      return { correct: false, message: 'Not authenticated' };
    }

    const result = await pinService.submitChallengeAnswer(
      huntId,
      challengeInfo.pinId,
      user.uid,
      user.displayName || 'Hunter',
      answer
    );

    if (result.correct && result.state) {
      setChallengeInfo(prev => prev ? { ...prev, state: result.state! } : null);
      // Challenge solved! The SignalR handler will handle the transition
    }

    return { correct: result.correct, message: result.message || '' };
  }, [huntId, challengeInfo]);

  // Handle captain releasing the challenge
  const handleSurpriseRelease = useCallback(async () => {
    if (!huntId || !challengeInfo) return;

    const user = useAuthStore.getState().user;
    if (!user) return;

    try {
      await pinService.unlockChallenge(huntId, challengeInfo.pinId, user.uid);
      // Reset state so another hunter can become captain
      setShowSurpriseReveal(false);
      setChallengeInfo(null);
      setCaptainInfo(null);
      setIsCaptain(false);
    } catch (err) {
      console.error('Failed to release challenge:', err);
    }
  }, [huntId, challengeInfo]);

  // Handle buddy pairing
  const handleOpenBuddyPicker = useCallback(async () => {
    if (!huntId) return;
    setShowBuddyPicker(true);
    setBuddyPickerLoading(true);
    setBuddyPickerError(null);
    try {
      const huntData = await huntService.getHunt(huntId);
      // Show only other hunters (not hiders, spectators, or self)
      const hunters = huntData.participants.filter(
        p => p.role === 'hunter' && p.userId !== currentUserId
      );
      setBuddyPickerParticipants(hunters);
    } catch (err) {
      setBuddyPickerError('Failed to load participants');
    } finally {
      setBuddyPickerLoading(false);
    }
  }, [huntId, currentUserId]);

  const handleSelectBuddy = useCallback(async (buddyUserId: string) => {
    if (!huntId) return;
    setBuddyPickerLoading(true);
    setBuddyPickerError(null);
    try {
      const result = await hunterService.pairBuddy(huntId, buddyUserId);
      setBuddyMode(true);
      setBuddyName(result.buddyDisplayName);
      setShowBuddyPicker(false);
      // Load pin info now that we're paired
      loadPinInfo();
    } catch (err) {
      setBuddyPickerError(err instanceof Error ? err.message : 'Failed to pair');
    } finally {
      setBuddyPickerLoading(false);
    }
  }, [huntId, loadPinInfo]);

  // Buddy mode: poll for pin discovery from buddy's location
  const buddyPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!buddyMode || !huntId || discoveredPin) {
      if (buddyPollRef.current) {
        clearInterval(buddyPollRef.current);
        buddyPollRef.current = null;
      }
      return;
    }
    // Poll every 5s to check if buddy's location brought us close enough to discover
    const poll = async () => {
      if (!pinInfo?.currentPin) return;
      try {
        await hunterService.discoverPin(huntId, pinInfo.currentPin.id, {
          // Send 0,0 — server will use our stored location (buddy's mirrored location)
          latitude: 0,
          longitude: 0,
        });
      } catch {
        // Expected to fail until buddy is in range — this is normal
      }
      // Re-fetch pin info to pick up discovery
      try {
        const info = await hunterService.getCurrentPin(huntId);
        if (info.isDiscovered && info.currentPin) {
          setDiscoveredPin({
            pinId: info.currentPin.id,
            hintPhotoUrl: info.hintPhotoUrl ?? info.currentPin.hintPhotoUrl,
            itemCount: info.currentPin.itemCount,
          });
          setFoundItemCount(info.foundItemsAtCurrentPin ?? 0);
          setPinInfo(info);
        }
      } catch {
        // non-fatal
      }
    };
    buddyPollRef.current = setInterval(poll, 5000);
    return () => {
      if (buddyPollRef.current) {
        clearInterval(buddyPollRef.current);
        buddyPollRef.current = null;
      }
    };
  }, [buddyMode, huntId, discoveredPin, pinInfo?.currentPin]);

  // Handle view stats
  const handleViewStats = useCallback(() => {
    navigate(`/hunts/${huntId}/stats`);
  }, [navigate, huntId]);

  // Show GPS denied screen — but NOT if already in buddy mode
  if (locationDenied && !buddyMode) {
    // Buddy picker overlay
    if (showBuddyPicker) {
      return (
        <div className="h-full bg-[#0A0A0F] flex flex-col p-6">
          <button
            onClick={() => setShowBuddyPicker(false)}
            className="text-gray-400 text-sm mb-4 self-start"
          >
            ← Back
          </button>
          <h1 className="text-white text-xl font-bold mb-2">Choose a Buddy</h1>
          <p className="text-gray-400 text-sm mb-6">
            Pick someone to walk with. Their GPS will guide you both to the pins — you'll hunt together as a team!
          </p>
          {buddyPickerLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin text-4xl">⟳</div>
            </div>
          )}
          {buddyPickerError && (
            <p className="text-red-400 text-center mb-4">{buddyPickerError}</p>
          )}
          {!buddyPickerLoading && buddyPickerParticipants.length === 0 && (
            <p className="text-gray-500 text-center mt-8">
              No other hunters have joined yet. Ask someone to join the hunt first!
            </p>
          )}
          <div className="space-y-3">
            {buddyPickerParticipants.map(p => (
              <button
                key={p.userId}
                onClick={() => handleSelectBuddy(p.userId)}
                disabled={buddyPickerLoading}
                className="w-full flex items-center gap-3 px-4 py-4 bg-surface rounded-lg hover:bg-surface-light transition-colors text-left"
              >
                <span className="text-2xl">🤝</span>
                <div>
                  <span className="text-white font-medium block">{p.displayName}</span>
                  <span className="text-gray-500 text-xs">Tap to pair</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Main GPS denied screen with buddy + spectator options
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center p-8">
        <div className="text-amber-400 text-6xl mb-4">📍</div>
        <h1 className="text-white text-xl font-bold mb-2">GPS Unavailable</h1>
        <p className="text-gray-400 text-center mb-2">
          Location access is unavailable on this device — but you can still hunt!
        </p>
        <p className="text-gray-500 text-center text-sm mb-6">
          Pair with someone who has GPS and walk together. You'll discover pins, find items, and solve challenges as a team.
        </p>
        <div className="space-y-3 w-full max-w-xs">
          <button
            onClick={handleOpenBuddyPicker}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 font-semibold transition-all"
          >
            🤝 Pair with a Buddy
          </button>
          <button
            onClick={() => navigate(`/spectator/${huntId}`, { replace: true })}
            className="w-full px-6 py-3 bg-surface text-text-secondary rounded-lg hover:bg-surface-light text-sm"
          >
            👁️ Watch as Spectator instead
          </button>
          <button
            onClick={loadPinInfo}
            className="w-full px-6 py-3 bg-transparent text-gray-600 hover:text-gray-400 text-xs"
          >
            Retry GPS Access
          </button>
        </div>
      </div>
    );
  }

  // Show error screen (non-GPS errors)
  if (error || geoError) {
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center p-8">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h1 className="text-white text-xl font-bold mb-2">Error</h1>
        <p className="text-gray-400 text-center mb-6">{error || geoError}</p>
        <button
          onClick={loadPinInfo}
          className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
        >
          Retry
        </button>
      </div>
    );
  }

  // Show hunt complete screen
  if (huntComplete) {
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center p-8 relative overflow-hidden">
        <VictoryCelebration />
        
        <VictoryHeader 
          title="VICTORY!"
          subtitle="You found all the pins and items!"
        />
        
        {completionTime && (
          <p className="text-gray-400 text-sm mt-4 z-10">
            Completed at {new Date(completionTime).toLocaleTimeString()}
          </p>
        )}
        
        <button
          onClick={handleViewStats}
          className="mt-8 px-10 py-5 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white text-xl font-bold rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg shadow-orange-500/50 z-10"
          style={{
            animation: 'pulse 2s infinite',
          }}
        >
          🏆 View Statistics
        </button>
      </div>
    );
  }

  // Show loading
  if (isLoading && !pinInfo) {
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center">
        <div className="animate-spin text-4xl mb-4">⟳</div>
        <p className="text-gray-400">Loading hunt...</p>
      </div>
    );
  }

  // Show compass or proximity view (GPS-based, no device compass permission needed)
  // In buddy mode, replace the compass with a team navigation view
  return (
    <div className="h-full bg-[#0A0A0F] flex flex-col overflow-hidden">
      {/* Connection warning banner */}
      <ConnectionStatusBanner
        status={connectionStatus}
        onRetry={() => forceReconnect()}
      />

      {/* Buddy mode banner */}
      {buddyMode && buddyName && (
        <div className="bg-emerald-900/60 border-b border-emerald-500/30 px-4 py-2 flex items-center gap-2">
          <span className="text-lg">🤝</span>
          <span className="text-emerald-300 text-sm font-medium">
            Following {buddyName}&apos;s GPS
          </span>
          <span className="text-emerald-600 text-xs ml-auto">
            Pin {(pinInfo?.currentPinIndex ?? 0) + 1} of {pinInfo?.totalPins ?? '?'}
          </span>
        </div>
      )}

      {/* Header - GPS accuracy only (not shown in buddy mode) */}
      {!buddyMode && distance !== null && !debouncedInProximity && position?.accuracy && (
        <header className="flex items-center justify-end px-4 py-2 border-b border-gray-800">
          <span className="text-gray-400 text-sm">
            GPS ±{position.accuracy.toFixed(0)}m
          </span>
        </header>
      )}

      {/* Item error toast */}
      {itemError && (
        <div className="absolute top-16 left-4 right-4 z-50 animate-pulse">
          <div className="bg-red-900/90 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-center text-sm font-medium shadow-lg">
            ⚠️ {itemError}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 flex flex-col ${(buddyMode ? discoveredPin : debouncedInProximity && discoveredPin) ? 'pb-32' : ''}`}>
        {buddyMode ? (
          /* Buddy mode: simplified view without compass */
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            {!discoveredPin ? (
              /* Walking to pin — waiting for buddy to bring us in range */
              <>
                <div className="text-6xl mb-6 animate-bounce">🚶</div>
                <h2 className="text-white text-xl font-bold mb-2">Walking to Pin {(pinInfo?.currentPinIndex ?? 0) + 1}</h2>
                <p className="text-gray-400 mb-4">
                  Stay close to <span className="text-emerald-400 font-semibold">{buddyName}</span> — when you reach the pin together, items will appear!
                </p>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <span className="animate-pulse">●</span>
                  Checking for nearby pins...
                </div>
              </>
            ) : (
              /* Pin discovered — show hint photo and items */
              <>
                <div className="text-5xl mb-4">🎯</div>
                <h2 className="text-green-400 text-xl font-bold mb-2">Pin Discovered!</h2>
                {discoveredPin.hintPhotoUrl && (
                  <img
                    src={discoveredPin.hintPhotoUrl}
                    alt="Hint"
                    className="w-48 h-48 object-cover rounded-xl border-2 border-yellow-500/40 mb-4"
                  />
                )}
                <p className="text-gray-400 text-sm mb-2">
                  Look around for hidden items near here!
                </p>
              </>
            )}
          </div>
        ) : (
          /* Normal GPS mode: compass navigation */
          <CompassDisplay
            deviceHeading={heading}
            targetBearing={bearing}
            distance={distance || 0}
            isLoading={geoLoading || !compassActive}
            error={compassError}
            inProximity={debouncedInProximity}
            hintPhotoUrl={discoveredPin?.hintPhotoUrl}
            otherHunters={otherHunters}
          />
        )}
      </div>

      {/* Sticky item buttons footer - in buddy mode, show when discovered; in GPS mode, show when in proximity */}
      {((buddyMode && discoveredPin) || (!buddyMode && debouncedInProximity && discoveredPin)) && discoveredPin && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0F]/95 backdrop-blur-sm border-t border-yellow-500/30 px-4 py-3">
          <div className="max-w-md mx-auto">
            {/* Item progress circles */}
            <div className="flex justify-center gap-3 mb-4">
              {Array.from({ length: discoveredPin.itemCount }, (_, index) => (
                <div
                  key={index}
                  className={`w-10 h-10 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                    index < foundItemCount
                      ? 'bg-green-500 border-green-500'
                      : 'bg-transparent border-white/40'
                  }`}
                  style={{
                    boxShadow: index < foundItemCount ? '0 0 12px rgba(34, 197, 94, 0.5)' : 'none',
                  }}
                >
                  {index < foundItemCount && (
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              ))}
            </div>
            
            {/* All items found - complete button */}
            {foundItemCount >= discoveredPin.itemCount ? (
              <button
                onClick={handlePinComplete}
                disabled={isLoading || awaitingValidation}
                className={`w-full py-4 px-6 rounded-lg font-bold text-lg text-white transition-all ${
                  awaitingValidation 
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 cursor-wait' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 active:scale-95'
                }`}
                style={{
                  animation: !isLoading && !awaitingValidation ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  boxShadow: awaitingValidation 
                    ? '0 0 20px rgba(59, 130, 246, 0.4)' 
                    : '0 0 20px rgba(34, 197, 94, 0.4)',
                }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⟳</span>
                    Processing...
                  </span>
                ) : awaitingValidation ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-pulse">⏳</span>
                    Waiting for Hider Validation...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    ✓ ALL FOUND — CONTINUE
                  </span>
                )}
              </button>
            ) : (
              /* Found Item button */
              <button
                onClick={handleItemFound}
                disabled={isLoading}
                className="w-full py-4 px-6 rounded-lg font-bold text-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 active:scale-95 transition-all"
                style={{
                  boxShadow: '0 0 20px rgba(245, 158, 11, 0.4)',
                }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⟳</span>
                    Recording...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    🎯 FOUND ITEM ({foundItemCount}/{discoveredPin.itemCount})
                  </span>
                )}
              </button>
            )}
            
            {/* Error message */}
            {itemError && (
              <p className="text-red-400 text-sm text-center mt-2">{itemError}</p>
            )}
          </div>
        </div>
      )}

      {/* Hunt deleted modal */}
      {huntDeletedInfo && (
        <HuntDeletedModal
          huntName={huntDeletedInfo.huntName}
          message={huntDeletedInfo.message}
          onDismiss={() => {
            // State already cleared by signal handler
            navigate('/', { replace: true });
          }}
        />
      )}

      {/* Challenge modal */}
      {showChallengeModal && challengeInfo && pinInfo && (
        <HunterChallengeModal
          pinId={challengeInfo.pinId}
          pinIndex={pinInfo.currentPinIndex}
          challengeType={challengeInfo.type}
          challengeQuestion={challengeInfo.question}
          challengeState={challengeInfo.state}
          currentUserId={currentUserId || ''}
          onLock={handleChallengeLock}
          onUnlock={handleChallengeUnlock}
          onSubmit={handleChallengeSubmit}
          onClose={handleChallengeClose}
        />
      )}

      {/* Challenge surprise reveal */}
      {showSurpriseReveal && challengeInfo && pinInfo && (
        <ChallengeSurpriseReveal
          pinIndex={pinInfo.currentPinIndex}
          challengeType={challengeInfo.type}
          challengeQuestion={challengeInfo.question}
          challengeState={challengeInfo.state}
          currentUserId={currentUserId || ''}
          isCaptain={isCaptain}
          captainName={captainInfo?.displayName || ''}
          onSubmit={handleSurpriseSubmit}
          onRelease={handleSurpriseRelease}
          onClose={handleChallengeClose}
        />
      )}
    </div>
  );
}

export default HunterPage;
